import React from 'react';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import { Input, Spin } from 'antd';
import { PRETTY_DATE_FORMAT } from '@owl-frontend/components';
import { fetchHome } from '../api/home/client';
import type { HomeData, HomeOwlTask, HomePolicy } from '../api/home/interface';
import OwlLogo from '../assets/owl-logo-purple.svg';
import { Locale } from '../context/AppProvider';
import { getRecents, RECENTS_CHANGED_EVENT } from './recentsStorage';
import { compareOwlTasks } from './utils';
import styles from './HomeContainer.module.scss';
import { PanelPill } from './PanelList/PanelList';

function fuzzyScore(query: string, text: string): number {
  const q = query.toLowerCase().trim();
  const t = (text || '').toLowerCase();
  if (!q) {
    return 0;
  }
  if (t.includes(q)) {
    return 1000 - t.indexOf(q);
  }
  let qi = 0;
  let score = 0;
  let streak = 0;
  let prev = -1;
  for (let ti = 0; ti < t.length && qi < q.length; ti += 1) {
    if (t[ti] === q[qi]) {
      streak = prev === ti - 1 ? streak + 1 : 0;
      score += 5 + streak * 3 + (ti === 0 || /\s/.test(t[ti - 1]) ? 4 : 0);
      prev = ti;
      qi += 1;
    }
  }
  return qi === q.length ? score : -1;
}

const EscHomeContainer: React.FC = () => {
  const { messages } = React.useContext(Locale);
  const navigate = useNavigate();
  const [data, setData] = React.useState<HomeData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [recents, setRecents] = React.useState(getRecents());

  React.useEffect(() => {
    const handler = () => setRecents(getRecents());
    window.addEventListener(RECENTS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(RECENTS_CHANGED_EVENT, handler);
  }, []);

  React.useEffect(() => {
    let live = true;
    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await fetchHome();
        if (live) {
          setData(res);
        }
      } catch {
        if (live) {
          setError(true);
        }
      } finally {
        if (live) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      live = false;
    };
  }, []);

  const allPolicies = React.useMemo(() => data?.policies ?? [], [data]);
  const open = React.useMemo(() => data?.flagged_by_owl ?? [], [data]);
  const orderedOpen = React.useMemo(
    () => [...open].sort(compareOwlTasks),
    [open]
  );

  const dueToday = React.useMemo(() => {
    const today = dayjs().format('YYYY-MM-DD');
    return open.filter(
      (t) => t.due && dayjs(t.due).format('YYYY-MM-DD') === today
    ).length;
  }, [open]);

  const policyByDossierId = React.useMemo(
    () => new Map(allPolicies.map((p) => [p.dossier_id, p])),
    [allPolicies]
  );

  const recentPolicies = React.useMemo(
    () =>
      recents
        .filter((r) => r.type === 'dossier' && r.dossierId)
        .map((r) => policyByDossierId.get(r.dossierId))
        .filter((p): p is HomePolicy => Boolean(p)),
    [recents, policyByDossierId]
  );

  const search = query.trim();
  const searching = search.length > 0;
  const policies = React.useMemo(() => {
    if (!searching) {
      return recentPolicies;
    }
    return allPolicies
      .map((p) => ({ p, s: fuzzyScore(search, p.name) }))
      .filter((r) => r.s >= 0)
      .sort((a, b) => b.s - a.s)
      .map((r) => r.p);
  }, [search, searching, allPolicies, recentPolicies]);

  const openPolicy = (p: HomePolicy) => navigate(`/claims/${p.dossier_id}`);
  const openOwlTask = (t: HomeOwlTask) =>
    navigate(`/claims/${t.dossier_id}?tab=tasks&left=task:${t.task_id}`);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        {messages['esc.home.load_error'] ??
          'Could not load your dashboard. Please refresh to try again.'}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.hero}>
        <span className={styles.mark}>
          <OwlLogo width={44} height={44} viewBox="0 0 12 12" />
        </span>
        <h1>{messages['esc.home.welcome'] ?? 'Welcome back'}</h1>
        <div className={styles.stats}>
          <span className={styles.stat}>
            <b>{open.length}</b>{' '}
            {open.length === 1 ? 'task flagged by Owl' : 'tasks flagged by Owl'}
          </span>
          <span
            className={dueToday ? `${styles.stat} ${styles.due}` : styles.stat}
          >
            <CalendarOutlined />
            <b>{dueToday}</b> due today
          </span>
        </div>
        <Input
          className={styles.search}
          size="large"
          allowClear
          prefix={<SearchOutlined />}
          placeholder={`Search all ${allPolicies.length} policies…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className={styles.board}>
        <div className={styles.sectionTitle}>
          {searching
            ? messages['esc.home.matching'] ?? 'Matching policies'
            : messages['esc.home.recent'] ?? 'Recent policies'}
          <span className={styles.sectionCount}>{policies.length}</span>
          {searching && (
            <span className={styles.sectionScope}>
              of {allPolicies.length} searched
            </span>
          )}
        </div>
        <div className={styles.polTable} role="table">
          <div className={styles.polHead} role="row">
            <span>Policy</span>
            <span>Active claims</span>
            <span>Flagged by Owl</span>
            <span>Last updated</span>
          </div>
          {policies.length === 0 ? (
            <div className={styles.empty}>
              <SearchOutlined />
              <span>
                {searching
                  ? `No policies match “${search}”.`
                  : allPolicies.length > 0
                  ? messages['esc.home.no_recent'] ??
                    'No recent policies — search above to open one.'
                  : messages['esc.home.no_policies'] ?? 'No policies yet.'}
              </span>
            </div>
          ) : (
            policies.map((p) => (
              <button
                type="button"
                key={p.dossier_id}
                className={styles.polRow}
                role="row"
                onClick={() => openPolicy(p)}
              >
                <span className={styles.polName}>
                  <FileTextOutlined />
                  <span className={styles.polNameText}>{p.name}</span>
                  {(p.active_claims > 0 || p.flagged_by_owl > 0) && (
                    <span className={styles.polActive}>Active</span>
                  )}
                </span>
                <span className={styles.polNum}>{p.active_claims}</span>
                <span className={styles.polNum}>
                  {p.flagged_by_owl ? (
                    <span className={styles.escBadge}>{p.flagged_by_owl}</span>
                  ) : (
                    <span className={styles.zero}>0</span>
                  )}
                </span>
                <span className={styles.polUpdated}>
                  {p.updated_at
                    ? dayjs(p.updated_at).format(PRETTY_DATE_FORMAT)
                    : '—'}
                </span>
                <RightOutlined className={styles.chev} />
              </button>
            ))
          )}
        </div>

        <div className={styles.sectionTitle}>
          {messages['esc.home.flagged_by_owl'] ?? 'Flagged by Owl'}
          <span className={styles.sectionCount}>{open.length}</span>
        </div>
        <div className={styles.escTable} role="table">
          <div className={styles.escHead} role="row">
            <span>Task</span>
            <span>Policy</span>
            <span>Priority</span>
            <span>Date</span>
          </div>
          {open.length === 0 ? (
            <div className={styles.empty}>
              <CheckCircleOutlined />
              <span>
                {messages['esc.home.no_flagged'] ??
                  "Nothing flagged by Owl — you're all caught up."}
              </span>
            </div>
          ) : (
            orderedOpen.map((t) => (
              <button
                type="button"
                key={t.task_id}
                className={styles.escRow}
                role="row"
                onClick={() => openOwlTask(t)}
              >
                <span className={styles.escMain}>
                  <span className={styles.escTitle}>{t.title}</span>
                  {t.preview && (
                    <span className={styles.escPrev}>{t.preview}</span>
                  )}
                </span>
                <span>
                  {t.policy_name && (
                    <span className={styles.policyPill}>
                      <FileTextOutlined />
                      {t.policy_name}
                    </span>
                  )}
                </span>
                <span>
                  <PanelPill tone={t.status === 'done' ? 'resolved' : 'open'}>
                    {t.priority}
                  </PanelPill>
                </span>
                <span className={styles.escDate}>
                  {t.due ? (
                    <>
                      <CalendarOutlined className={styles.dueIcon} />
                      {dayjs(t.due).format(PRETTY_DATE_FORMAT)}
                    </>
                  ) : t.created_at ? (
                    dayjs(t.created_at).format(PRETTY_DATE_FORMAT)
                  ) : null}
                </span>
                <RightOutlined className={styles.chev} />
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EscHomeContainer;
